import { Injectable, signal } from '@angular/core';

export type FontSizeLevel = 'small' | 'medium' | 'large' | 'custom';

const LEVEL_KEY = 'app-font-size';
const CUSTOM_KEY = 'app-font-size-custom';
const VALID_LEVELS: FontSizeLevel[] = ['small', 'medium', 'large', 'custom'];

const PRESET_MULTIPLIERS: Record<Exclude<FontSizeLevel, 'custom'>, number> = {
  small: 0.875,
  medium: 1.0,
  large: 1.125,
};

interface ScaleTier {
  prop: string;
  baseRem: number;
}

const SCALE_TIERS: ScaleTier[] = [
  { prop: '--fs-2xs', baseRem: 0.65 },
  { prop: '--fs-xs', baseRem: 0.75 },
  { prop: '--fs-sm', baseRem: 0.8125 },
  { prop: '--fs-base', baseRem: 0.875 },
  { prop: '--fs-lg', baseRem: 1.0 },
  { prop: '--fs-xl', baseRem: 1.125 },
  { prop: '--fs-2xl', baseRem: 1.25 },
];

@Injectable({ providedIn: 'root' })
export class FontSizeService {
  readonly currentLevel = signal<FontSizeLevel>(this.loadLevel());
  readonly customBaseSize = signal<number>(this.loadCustomBase());

  constructor() {
    this.applyCurrentScale();
  }

  setLevel(level: FontSizeLevel): void {
    this.currentLevel.set(level);
    localStorage.setItem(LEVEL_KEY, level);
    this.applyCurrentScale();
  }

  setCustomBase(px: number): void {
    const clamped = Math.max(10, Math.min(20, px));
    this.customBaseSize.set(clamped);
    localStorage.setItem(CUSTOM_KEY, String(clamped));
    if (this.currentLevel() === 'custom') {
      this.applyCurrentScale();
    }
  }

  private applyCurrentScale(): void {
    const level = this.currentLevel();
    if (level === 'custom') {
      const basePx = this.customBaseSize();
      const multiplier = basePx / 14;
      this.applyScale(multiplier);
    } else {
      this.applyScale(PRESET_MULTIPLIERS[level]);
    }
  }

  private applyScale(multiplier: number): void {
    const root = document.documentElement;
    for (const tier of SCALE_TIERS) {
      const value = tier.baseRem * multiplier;
      root.style.setProperty(tier.prop, `${parseFloat(value.toFixed(4))}rem`);
    }
  }

  private loadLevel(): FontSizeLevel {
    const stored = localStorage.getItem(LEVEL_KEY) as FontSizeLevel | null;
    return stored && VALID_LEVELS.includes(stored) ? stored : 'medium';
  }

  private loadCustomBase(): number {
    const stored = localStorage.getItem(CUSTOM_KEY);
    if (!stored) return 14;
    const parsed = parseFloat(stored);
    return isNaN(parsed) ? 14 : Math.max(10, Math.min(20, parsed));
  }
}
